import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaKey, FaPlus, FaEye, FaEyeSlash, FaCopy, FaEdit, FaTrash,
    FaServer, FaNetworkWired, FaGlobe, FaDatabase, FaCog, FaCheck,
    FaSearch, FaTimes, FaLock, FaFilter, FaUser, FaHashtag, FaTag, FaClock,
    FaShieldAlt, FaUnlock, FaStar, FaExclamationTriangle, FaMagic,
    FaHistory, FaChevronDown, FaChevronUp, FaSyncAlt
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Modal } from './common';
import CredentialModal from './CredentialModal';
import axiosInstance from '../utils/axiosConfig';
import './CredentialsView.css';

// Tiempo de inactividad antes de bloquear (5 minutos en ms)
const LOCK_TIMEOUT = 5 * 60 * 1000;

// Mapeo de iconos por nombre
const iconMap = {
    Server: FaServer,
    Router: FaNetworkWired,
    Globe: FaGlobe,
    Database: FaDatabase,
    Cog: FaCog,
    Key: FaKey
};

// Componente de pantalla de bloqueo
const VaultLockScreen = ({ onUnlock, remainingTime }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Verificar la contraseña del usuario actual
            const response = await axiosInstance.post('/api/auth/verify-password', { password });
            if (response.data.valid) {
                onUnlock();
            } else {
                setError('Contraseña incorrecta');
            }
        } catch (err) {
            // Manejar específicamente el error de contraseña incorrecta
            if (err.response?.status === 401) {
                // Prevenir que el interceptor redirija - mostramos error local
                setError('Contraseña incorrecta');
            } else if (err.response?.status === 404) {
                // Si el endpoint no existe, intentamos verificar con re-login
                try {
                    const user = JSON.parse(localStorage.getItem('user'));
                    const loginResponse = await axiosInstance.post('/api/auth/login', {
                        username: user?.username,
                        password: password
                    });
                    if (loginResponse.data.token) {
                        // Actualizar token
                        localStorage.setItem('token', loginResponse.data.token);
                        onUnlock();
                    }
                } catch (loginErr) {
                    setError('Contraseña incorrecta');
                }
            } else {
                setError('Error al verificar. Intenta de nuevo.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[500px] py-12">
            <motion.div
                className="w-full max-w-md"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                {/* Icono de candado */}
                <div className="flex justify-center mb-6">
                    <motion.div
                        className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-lg"
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                    >
                        <FaLock className="text-4xl text-gray-500" />
                    </motion.div>
                </div>

                {/* Título */}
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Bóveda Bloqueada</h2>
                    <p className="text-gray-500 text-sm">
                        Ingresa tu contraseña para acceder a las credenciales
                    </p>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaKey className="inline mr-2 text-gray-400" />
                            Contraseña de tu cuenta
                        </label>
                        <div className="relative">
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/20 outline-none transition-all text-gray-800"
                                placeholder="••••••••"
                                autoFocus
                                required
                            />
                            <FaShieldAlt className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                        </div>
                    </div>

                    {error && (
                        <motion.div
                            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <FaTimes className="flex-shrink-0" />
                            {error}
                        </motion.div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || !password}
                        className="w-full py-3 bg-sura-blue text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-sura-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Verificando...
                            </>
                        ) : (
                            <>
                                <FaUnlock />
                                Desbloquear Bóveda
                            </>
                        )}
                    </button>
                </form>

                {/* Info de seguridad */}
                <div className="mt-6 text-center">
                    <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                        <FaShieldAlt />
                        La bóveda se bloqueará automáticamente tras 5 minutos de inactividad
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

// Modal de visualización de credencial
const ViewCredentialModal = ({ credential, categories, onClose, onEdit }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [revealedHistoryPasswords, setRevealedHistoryPasswords] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.get(`/api/credentials/${credential.id}`);
                setData(response.data);
            } catch (err) {
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [credential.id]);

    const copyToClipboard = async (text, field) => {
        await navigator.clipboard.writeText(text);
        setCopied(field);
        setTimeout(() => setCopied(null), 2000);
    };

    const fetchHistory = async () => {
        if (history.length > 0) return;
        setLoadingHistory(true);
        try {
            const response = await axiosInstance.get(`/api/credentials/${credential.id}/password-history`);
            setHistory(response.data);
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const toggleHistory = () => {
        if (!showHistory) fetchHistory();
        setShowHistory(!showHistory);
    };

    const getCategoryIcon = (iconName) => iconMap[iconName] || FaKey;

    const category = categories.find(c => c.id === data?.categoria_id);

    const InfoRow = ({ icon: Icon, label, value, copyable, isPassword }) => {
        if (!value) return null;

        return (
            <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex-shrink-0">
                    <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                        {isPassword ? (
                            <code className="text-sm font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                                {showPassword ? value : '••••••••••••'}
                            </code>
                        ) : (
                            <span className="text-sm font-medium text-gray-800 break-all">{value}</span>
                        )}
                        {copyable && (
                            <button
                                onClick={() => copyToClipboard(value, label)}
                                className={`p-1.5 rounded transition-colors flex-shrink-0 ${copied === label
                                    ? 'text-green-500 bg-green-50'
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                    }`}
                                title="Copiar"
                            >
                                {copied === label ? <FaCheck size={12} /> : <FaCopy size={12} />}
                            </button>
                        )}
                        {isPassword && (
                            <button
                                onClick={() => setShowPassword(!showPassword)}
                                className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                                title={showPassword ? "Ocultar" : "Mostrar"}
                            >
                                {showPassword ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={true} onClose={onClose} size="lg">
            <Modal.Header onClose={onClose}>
                Detalle de Credencial
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-sura-blue rounded-full animate-spin" />
                    </div>
                ) : data ? (
                    <div className="space-y-6">
                        {/* Header con nombre y categoría */}
                        <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-sura-blue/10 text-sura-blue">
                                {category ? React.createElement(getCategoryIcon(category.icono), { size: 24 }) : <FaKey size={24} />}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900">{data.nombre}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                    {category && (
                                        <span
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                            style={{
                                                backgroundColor: `${category.color}15`,
                                                color: category.color
                                            }}
                                        >
                                            {category.nombre}
                                        </span>
                                    )}
                                    {data.ambiente && (
                                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${data.ambiente.toLowerCase().includes('prod') ? 'bg-red-100 text-red-700' :
                                            data.ambiente.toLowerCase().includes('dev') ? 'bg-green-100 text-green-700' :
                                                data.ambiente.toLowerCase() === 'qa' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-gray-100 text-gray-700'
                                            }`}>
                                            {data.ambiente}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Credenciales de acceso */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <FaLock size={12} />
                                    Credenciales de Acceso
                                </h4>
                            </div>
                            <div className="px-4">
                                <InfoRow icon={FaUser} label="Usuario" value={data.username} copyable />
                                <InfoRow icon={FaLock} label="Contraseña" value={data.password} copyable isPassword />
                            </div>
                        </div>

                        {/* Datos de conexión */}
                        {(data.url || data.ip_address || data.puerto || data.dominio) && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        <FaServer size={12} />
                                        Datos de Conexión
                                    </h4>
                                </div>
                                <div className="px-4">
                                    <InfoRow icon={FaGlobe} label="URL" value={data.url} copyable />
                                    <InfoRow icon={FaServer} label="Dirección IP" value={data.ip_address} copyable />
                                    <InfoRow icon={FaHashtag} label="Puerto" value={data.puerto} copyable />
                                    <InfoRow icon={FaNetworkWired} label="Dominio" value={data.dominio} copyable />
                                </div>
                            </div>
                        )}

                        {/* Tags */}
                        {data.tags && data.tags.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        <FaTag size={12} />
                                        Tags
                                    </h4>
                                </div>
                                <div className="p-4 flex flex-wrap gap-2">
                                    {data.tags.map((tag, i) => (
                                        <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notas */}
                        {data.notas && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-700">Notas</h4>
                                </div>
                                <div className="p-4">
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.notas}</p>
                                </div>
                            </div>
                        )}

                        {/* Historial de contraseñas */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <button
                                onClick={toggleHistory}
                                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                            >
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <FaHistory size={12} />
                                    Historial de Contraseñas
                                </h4>
                                {showHistory ? <FaChevronUp size={12} className="text-gray-400" /> : <FaChevronDown size={12} className="text-gray-400" />}
                            </button>
                            <AnimatePresence>
                                {showHistory && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="p-4">
                                            {loadingHistory ? (
                                                <div className="flex items-center justify-center py-4">
                                                    <div className="w-5 h-5 border-2 border-gray-200 border-t-sura-blue rounded-full animate-spin" />
                                                </div>
                                            ) : history.length === 0 ? (
                                                <p className="text-sm text-gray-400 text-center py-3">Sin cambios registrados</p>
                                            ) : (
                                                <div className="space-y-3">
                                                    {history.map((entry, i) => (
                                                        <div key={entry.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <code className="text-xs font-mono text-gray-700 bg-white px-2 py-1 rounded border">
                                                                        {revealedHistoryPasswords[entry.id] ? entry.password : '••••••••'}
                                                                    </code>
                                                                    <button
                                                                        onClick={() => setRevealedHistoryPasswords(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                                                                        className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                                                    >
                                                                        {revealedHistoryPasswords[entry.id] ? <FaEyeSlash size={10} /> : <FaEye size={10} />}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => copyToClipboard(entry.password, `history-${entry.id}`)}
                                                                        className={`p-1 rounded transition-colors ${copied === `history-${entry.id}` ? 'text-green-500' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                                                                    >
                                                                        {copied === `history-${entry.id}` ? <FaCheck size={10} /> : <FaCopy size={10} />}
                                                                    </button>
                                                                </div>
                                                                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                                                                    <span className="flex items-center gap-1">
                                                                        <FaClock size={9} />
                                                                        {new Date(entry.changed_at).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                    </span>
                                                                    {entry.changed_by && (
                                                                        <span className="flex items-center gap-1">
                                                                            <FaUser size={9} />
                                                                            {entry.changed_by}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center justify-between text-xs text-gray-400 px-1">
                            <span className="flex items-center gap-1">
                                <FaClock size={10} />
                                Creado por: {data.created_by || 'N/A'}
                            </span>
                            {data.updated_by && (
                                <span>Modificado por: {data.updated_by}</span>
                            )}
                        </div>
                    </div>
                ) : (
                    <p className="text-center text-gray-500 py-8">Error cargando datos</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-secondary"
                >
                    Cerrar
                </button>
                <button
                    type="button"
                    onClick={() => { onClose(); onEdit(); }}
                    className="btn btn-primary flex items-center gap-2"
                >
                    <FaEdit size={14} />
                    Editar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const CredentialsView = () => {
    const [credentials, setCredentials] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCredential, setEditingCredential] = useState(null);
    const [viewingCredential, setViewingCredential] = useState(null);
    const [revealedPasswords, setRevealedPasswords] = useState({});
    const [copiedId, setCopiedId] = useState(null);
    const [decryptedPasswords, setDecryptedPasswords] = useState({});

    // Auditoría
    const [auditData, setAuditData] = useState(null);
    const [auditExpanded, setAuditExpanded] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    // Estado de bloqueo de la bóveda
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [remainingTime, setRemainingTime] = useState(LOCK_TIMEOUT);
    const lastActivityRef = useRef(Date.now());
    const timerRef = useRef(null);

    // Función para registrar actividad
    const registerActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
        setRemainingTime(LOCK_TIMEOUT);
    }, []);

    // Timer para auto-bloqueo
    useEffect(() => {
        if (!isUnlocked) return;

        const checkInactivity = () => {
            const elapsed = Date.now() - lastActivityRef.current;
            const remaining = LOCK_TIMEOUT - elapsed;

            if (remaining <= 0) {
                // Bloquear la bóveda
                setIsUnlocked(false);
                setDecryptedPasswords({});
                setRevealedPasswords({});
                setRemainingTime(LOCK_TIMEOUT);
            } else {
                setRemainingTime(remaining);
            }
        };

        timerRef.current = setInterval(checkInactivity, 1000);

        // Eventos de actividad
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(event => {
            document.addEventListener(event, registerActivity);
        });

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            events.forEach(event => {
                document.removeEventListener(event, registerActivity);
            });
        };
    }, [isUnlocked, registerActivity]);

    // Función para desbloquear
    const handleUnlock = useCallback(() => {
        setIsUnlocked(true);
        lastActivityRef.current = Date.now();
        setRemainingTime(LOCK_TIMEOUT);
    }, []);

    // Función para bloquear manualmente
    const handleLock = useCallback(() => {
        setIsUnlocked(false);
        setDecryptedPasswords({});
        setRevealedPasswords({});
    }, []);

    // Formatear tiempo restante
    const formatRemainingTime = () => {
        const minutes = Math.floor(remainingTime / 60000);
        const seconds = Math.floor((remainingTime % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/credentials/categories');
            setCategories(response.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    }, []);

    const fetchCredentials = useCallback(async () => {
        if (!isUnlocked) return;

        try {
            setLoading(true);
            const params = {};
            if (selectedCategory) params.categoria_id = selectedCategory;
            if (globalFilter) params.search = globalFilter;

            const response = await axiosInstance.get('/api/credentials', { params });
            setCredentials(response.data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedCategory, globalFilter, isUnlocked]);

    const fetchAudit = useCallback(async () => {
        if (!isUnlocked) return;
        try {
            const response = await axiosInstance.get('/api/credentials/audit');
            setAuditData(response.data);
        } catch (err) {
            console.error('Error fetching audit:', err);
        }
    }, [isUnlocked]);

    useEffect(() => {
        if (isUnlocked) {
            fetchCategories();
            fetchAudit();
        }
    }, [fetchCategories, fetchAudit, isUnlocked]);

    useEffect(() => {
        if (!isUnlocked) return;

        const debounce = setTimeout(() => {
            fetchCredentials();
        }, 300);
        return () => clearTimeout(debounce);
    }, [fetchCredentials, isUnlocked]);

    const fetchDecryptedPassword = async (id) => {
        try {
            const response = await axiosInstance.get(`/api/credentials/${id}`);
            setDecryptedPasswords(prev => ({ ...prev, [id]: response.data.password }));
            return response.data.password;
        } catch (err) {
            console.error('Error fetching password:', err);
            return null;
        }
    };

    const togglePasswordVisibility = async (id) => {
        registerActivity();
        if (!revealedPasswords[id]) {
            if (!decryptedPasswords[id]) {
                await fetchDecryptedPassword(id);
            }
        }
        setRevealedPasswords(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const copyToClipboard = async (id) => {
        registerActivity();
        let password = decryptedPasswords[id];
        if (!password) {
            password = await fetchDecryptedPassword(id);
        }
        if (password) {
            await navigator.clipboard.writeText(password);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        }
    };

    const toggleFavorite = async (id, currentState) => {
        try {
            await axiosInstance.patch(`/api/credentials/${id}/favorite`, { is_favorite: !currentState });
            setCredentials(prev => prev.map(c =>
                c.id === id ? { ...c, is_favorite: !currentState } : c
            ).sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0) || a.nombre.localeCompare(b.nombre)));
        } catch (err) {
            console.error('Error toggling favorite:', err);
        }
    };

    const requestAiAnalysis = async () => {
        if (!auditData) return;
        setAiLoading(true);
        try {
            const response = await axiosInstance.post('/api/credentials/audit/ai-analysis', { audit_data: auditData });
            setAiAnalysis(response.data.analysis);
        } catch (err) {
            console.error('Error AI analysis:', err);
            setAiAnalysis('Error al generar el análisis. Intenta de nuevo.');
        } finally {
            setAiLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta credencial?')) return;

        try {
            await axiosInstance.delete(`/api/credentials/${id}`);
            fetchCredentials();
            fetchCategories();
        } catch (err) {
            console.error('Error deleting credential:', err);
        }
    };

    const handleSave = () => {
        setShowModal(false);
        setEditingCredential(null);
        fetchCredentials();
        fetchCategories();
        fetchAudit();
        setAiAnalysis(null);
    };

    const getCategoryIcon = (iconName) => {
        return iconMap[iconName] || FaKey;
    };

    // Filtrar credenciales localmente para la búsqueda
    const filteredCredentials = useMemo(() => {
        if (!globalFilter) return credentials;
        const search = globalFilter.toLowerCase();
        return credentials.filter(cred =>
            cred.nombre?.toLowerCase().includes(search) ||
            cred.username?.toLowerCase().includes(search) ||
            cred.url?.toLowerCase().includes(search) ||
            cred.ip_address?.toLowerCase().includes(search) ||
            cred.categoria?.toLowerCase().includes(search)
        );
    }, [credentials, globalFilter]);

    // Si la bóveda está bloqueada, mostrar pantalla de bloqueo
    if (!isUnlocked) {
        return (
            <div className="credentials-view">
                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <VaultLockScreen onUnlock={handleUnlock} remainingTime={remainingTime} />
                </div>
            </div>
        );
    }

    return (
        <div className="credentials-view">
            {/* Controles de tabla - Estilo TableView */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                    <div className="flex flex-wrap gap-4 items-center">
                        {/* Indicador de estado y timer */}
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                            <FaUnlock className="text-green-500" size={12} />
                            <span className="text-xs font-medium text-green-700">Desbloqueada</span>
                            <span className="text-xs text-green-600 font-mono">{formatRemainingTime()}</span>
                            <button
                                onClick={handleLock}
                                className="ml-1 p-1 text-green-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Bloquear bóveda"
                            >
                                <FaLock size={10} />
                            </button>
                        </div>

                        {/* Búsqueda */}
                        <motion.div
                            className="relative flex-1 min-w-[280px] max-w-[420px]"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className={`relative flex items-center bg-white rounded-xl border-2 transition-all duration-300 overflow-hidden ${globalFilter ? 'border-sura-blue shadow-lg shadow-sura-blue/10' : 'border-gray-200 hover:border-gray-300'}`}>
                                <div className="pl-4 text-gray-400">
                                    <FaSearch size={16} />
                                </div>
                                <input
                                    type="text"
                                    value={globalFilter}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                    placeholder="Buscar credenciales..."
                                    className="flex-1 px-3 py-3 bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400"
                                />
                                <AnimatePresence>
                                    {globalFilter && (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.8 }}
                                            className="flex items-center gap-2 pr-3"
                                        >
                                            <span className="text-xs font-semibold text-sura-blue bg-sura-blue/10 px-2 py-1 rounded-full">
                                                {filteredCredentials.length}
                                            </span>
                                            <motion.button
                                                onClick={() => setGlobalFilter('')}
                                                className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                whileHover={{ scale: 1.2, rotate: 90 }}
                                                whileTap={{ scale: 0.9 }}
                                            >
                                                <FaTimes size={12} />
                                            </motion.button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>

                        {/* Filtro de categoría */}
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="flex items-center gap-1 text-gray-400 mr-1">
                                <FaFilter size={12} />
                                <span className="text-xs font-medium uppercase tracking-wide">Filtros</span>
                            </div>

                            <div className="relative">
                                <select
                                    value={selectedCategory}
                                    onChange={e => setSelectedCategory(e.target.value)}
                                    className={`appearance-none bg-white pl-4 pr-10 py-2.5 text-sm font-medium text-gray-700 outline-none cursor-pointer min-w-[160px] rounded-xl border-2 transition-all ${selectedCategory ? 'border-sura-blue' : 'border-gray-200 hover:border-gray-300'}`}
                                >
                                    <option value="">Todas las categorías</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            {selectedCategory && (
                                <motion.button
                                    className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                                    onClick={() => setSelectedCategory('')}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                >
                                    <FaTimes size={10} />
                                    Limpiar
                                </motion.button>
                            )}
                        </div>

                        {/* Botón agregar */}
                        <motion.button
                            className="ml-auto flex items-center gap-2 px-4 py-2.5 bg-sura-blue text-white rounded-xl font-medium text-sm hover:bg-sura-blue-dark transition-colors"
                            onClick={() => { setEditingCredential(null); setShowModal(true); }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <FaPlus size={14} />
                            Nueva Credencial
                        </motion.button>
                    </div>
                </div>

                {/* Panel de Auditoría */}
                {auditData && (auditData.summary.weak > 0 || auditData.summary.duplicated > 0 || auditData.summary.not_rotated > 0) && (
                    <div className="border-b border-gray-100">
                        <div
                            className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => setAuditExpanded(!auditExpanded)}
                        >
                            <div className="flex items-center gap-3">
                                <FaShieldAlt size={14} className="text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">Auditoría de Seguridad</span>
                                <div className="flex items-center gap-2">
                                    {auditData.summary.weak > 0 && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                            {auditData.summary.weak} débiles
                                        </span>
                                    )}
                                    {auditData.summary.duplicated > 0 && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                            {auditData.summary.duplicated} duplicadas
                                        </span>
                                    )}
                                    {auditData.summary.not_rotated > 0 && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                            {auditData.summary.not_rotated} sin rotar
                                        </span>
                                    )}
                                </div>
                            </div>
                            {auditExpanded ? <FaChevronUp size={12} className="text-gray-400" /> : <FaChevronDown size={12} className="text-gray-400" />}
                        </div>

                        <AnimatePresence>
                            {auditExpanded && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-5 pb-4 space-y-4">
                                        {/* Contraseñas débiles */}
                                        {auditData.weak.length > 0 && (
                                            <div>
                                                <h5 className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                                    <FaExclamationTriangle size={10} />
                                                    Contraseñas Débiles
                                                </h5>
                                                <div className="space-y-1.5">
                                                    {auditData.weak.map(item => (
                                                        <div key={item.id} className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg text-sm">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-gray-800">{item.nombre}</span>
                                                                {item.categoria && <span className="text-xs text-gray-400">({item.categoria})</span>}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-red-600">{item.issues.join(', ')}</span>
                                                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${item.score < 30 ? 'bg-red-200 text-red-800' : 'bg-orange-200 text-orange-800'}`}>
                                                                    {item.score}/100
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Contraseñas duplicadas */}
                                        {auditData.duplicated.length > 0 && (
                                            <div>
                                                <h5 className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                                    <FaCopy size={10} />
                                                    Contraseñas Duplicadas
                                                </h5>
                                                <div className="space-y-1.5">
                                                    {auditData.duplicated.map((group, i) => (
                                                        <div key={i} className="p-2.5 bg-orange-50 rounded-lg text-sm">
                                                            <span className="text-gray-700">{group.group.join(' = ')}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Sin rotar */}
                                        {auditData.not_rotated.length > 0 && (
                                            <div>
                                                <h5 className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                                    <FaClock size={10} />
                                                    Sin Rotar ({'>'}90 días)
                                                </h5>
                                                <div className="space-y-1.5">
                                                    {auditData.not_rotated.map(item => (
                                                        <div key={item.id} className="flex items-center justify-between p-2.5 bg-yellow-50 rounded-lg text-sm">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-gray-800">{item.nombre}</span>
                                                                {item.categoria && <span className="text-xs text-gray-400">({item.categoria})</span>}
                                                            </div>
                                                            <span className="text-xs font-medium text-yellow-700">{item.days_since_change} días</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Botón IA */}
                                        <div className="pt-2 border-t border-gray-100">
                                            {!aiAnalysis ? (
                                                <button
                                                    onClick={requestAiAnalysis}
                                                    disabled={aiLoading}
                                                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors disabled:opacity-50"
                                                >
                                                    {aiLoading ? (
                                                        <>
                                                            <FaSyncAlt size={12} className="animate-spin" />
                                                            Analizando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaMagic size={12} />
                                                            Analizar con IA
                                                        </>
                                                    )}
                                                </button>
                                            ) : (
                                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <FaMagic size={12} className="text-purple-600" />
                                                        <span className="text-sm font-semibold text-purple-700">Análisis IA</span>
                                                    </div>
                                                    <div className="prose prose-sm max-w-none text-gray-700 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-bold [&_h2]:font-semibold [&_h3]:font-semibold [&_p]:text-sm [&_li]:text-sm [&_strong]:text-gray-900">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiAnalysis}</ReactMarkdown>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {/* Badge bóveda saludable */}
                {auditData && auditData.summary.weak === 0 && auditData.summary.duplicated === 0 && auditData.summary.not_rotated === 0 && (
                    <div className="px-5 py-2 border-b border-gray-100">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <FaShieldAlt size={10} />
                            Bóveda saludable
                        </span>
                    </div>
                )}

                {/* Tabla */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                        <motion.div
                            className="w-12 h-12 rounded-full border-3 border-gray-200 border-t-sura-blue"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        <p className="mt-4 text-sm font-medium">Cargando credenciales...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-16 text-red-500">
                        <FaLock size={32} className="mb-3 text-red-400" />
                        <p className="font-medium">{error}</p>
                    </div>
                ) : filteredCredentials.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <FaKey size={48} className="mb-4" />
                        <h3 className="text-lg font-semibold text-gray-600">No hay credenciales</h3>
                        <p className="text-sm text-gray-500 mb-4">Agrega tu primera credencial para empezar</p>
                        <button
                            className="flex items-center gap-2 px-4 py-2 bg-sura-blue text-white rounded-lg font-medium text-sm"
                            onClick={() => { setEditingCredential(null); setShowModal(true); }}
                        >
                            <FaPlus size={14} />
                            Agregar Credencial
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table-standard">
                            <thead>
                                <tr>
                                    <th className="w-10"></th>
                                    <th>Nombre</th>
                                    <th>Usuario</th>
                                    <th>Contraseña</th>
                                    <th>URL / IP</th>
                                    <th>Categoría</th>
                                    <th>Ambiente</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCredentials.map((cred) => (
                                    <tr key={cred.id}>
                                        <td>
                                            <button
                                                onClick={() => toggleFavorite(cred.id, cred.is_favorite)}
                                                className={`p-1.5 rounded transition-colors ${cred.is_favorite ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                                                title={cred.is_favorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                                            >
                                                <FaStar size={14} />
                                            </button>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2 font-medium text-gray-900">
                                                <FaLock size={14} className="text-gray-400" />
                                                {cred.nombre}
                                            </div>
                                        </td>
                                        <td>
                                            <code className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700 font-mono">
                                                {cred.username || '-'}
                                            </code>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-sm text-gray-500 min-w-[80px]">
                                                    {revealedPasswords[cred.id] ? (
                                                        <span className="text-gray-900 text-xs bg-gray-100 px-2 py-1 rounded">
                                                            {decryptedPasswords[cred.id] || '••••••••'}
                                                        </span>
                                                    ) : (
                                                        '••••••••'
                                                    )}
                                                </span>
                                                <button
                                                    className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                                    onClick={() => togglePasswordVisibility(cred.id)}
                                                    title={revealedPasswords[cred.id] ? "Ocultar" : "Mostrar"}
                                                >
                                                    {revealedPasswords[cred.id] ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                                </button>
                                                <button
                                                    className={`p-1.5 rounded transition-colors ${copiedId === cred.id ? 'text-green-500 bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                                                    onClick={() => copyToClipboard(cred.id)}
                                                    title="Copiar"
                                                >
                                                    {copiedId === cred.id ? <FaCheck size={14} /> : <FaCopy size={14} />}
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-gray-600 font-mono text-xs">
                                                {cred.url || cred.ip_address || '-'}
                                                {cred.puerto && `:${cred.puerto}`}
                                            </span>
                                        </td>
                                        <td>
                                            {cred.categoria && (
                                                <span
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                                    style={{
                                                        backgroundColor: `${cred.categoria_color}15`,
                                                        color: cred.categoria_color
                                                    }}
                                                >
                                                    {React.createElement(getCategoryIcon(categories.find(c => c.nombre === cred.categoria)?.icono), { size: 10 })}
                                                    {cred.categoria}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {cred.ambiente && (
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cred.ambiente.toLowerCase().includes('prod') ? 'bg-red-100 text-red-700' :
                                                    cred.ambiente.toLowerCase().includes('dev') ? 'bg-green-100 text-green-700' :
                                                        cred.ambiente.toLowerCase() === 'qa' ? 'bg-yellow-100 text-yellow-700' :
                                                            'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {cred.ambiente}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    className="p-2 rounded text-gray-400 hover:text-sura-blue hover:bg-blue-50 transition-colors"
                                                    onClick={() => setViewingCredential(cred)}
                                                    title="Ver detalle"
                                                >
                                                    <FaEye size={14} />
                                                </button>
                                                <button
                                                    className="p-2 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                                    onClick={() => { setEditingCredential(cred); setShowModal(true); }}
                                                    title="Editar"
                                                >
                                                    <FaEdit size={14} />
                                                </button>
                                                <button
                                                    className="p-2 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                    onClick={() => handleDelete(cred.id)}
                                                    title="Eliminar"
                                                >
                                                    <FaTrash size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de edición/creación */}
            {showModal && (
                <CredentialModal
                    credential={editingCredential}
                    categories={categories}
                    onClose={() => { setShowModal(false); setEditingCredential(null); }}
                    onSave={handleSave}
                />
            )}

            {/* Modal de visualización */}
            {viewingCredential && (
                <ViewCredentialModal
                    credential={viewingCredential}
                    categories={categories}
                    onClose={() => setViewingCredential(null)}
                    onEdit={() => { setEditingCredential(viewingCredential); setShowModal(true); }}
                />
            )}
        </div>
    );
};

export default CredentialsView;
